import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	arrayMove,
} from "@dnd-kit/sortable";
import { SortableQuestionRow } from "newui/features/Settings/SubmissionComplianceTab/SortableQuestionRow";
import type { ComplianceQuestion } from "newui/features/Rfx/Evaluation/types/compliance";

export interface QuestionsListProps {
	questions: ComplianceQuestion[];
	onEdit: (id: number, question: string) => void;
	onDelete: (id: number) => void;
	onReorder: (updated: ComplianceQuestion[]) => void;
}

export const QuestionsList = ({
	questions,
	onEdit,
	onDelete,
	onReorder,
}: QuestionsListProps) => {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) {
			return;
		}
		const oldIndex = questions.findIndex((q) => q.id === active.id);
		const newIndex = questions.findIndex((q) => q.id === over.id);
		if (oldIndex === -1 || newIndex === -1) {
			return;
		}
		onReorder(arrayMove(questions, oldIndex, newIndex));
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={questions.map((q) => q.id)}
				strategy={verticalListSortingStrategy}
			>
				<div
					className="overflow-hidden rounded-md border border-gray-200"
					data-testid="pre-compliance-questions-list"
				>
					{questions.map((question) => (
						<SortableQuestionRow
							key={question.id}
							question={question}
							onEdit={onEdit}
							onDelete={onDelete}
						/>
					))}
				</div>
			</SortableContext>
		</DndContext>
	);
};
